import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { BsArrowRight } from "react-icons/bs";
import { motion, useInView } from "framer-motion";
import "./HomeSolutions.css";
import financeImg from '../../assets/Home/Service/comp4.webp';
import projectImg from '../../assets/Home/Service/comp2.webp';
import epcImg from '../../assets/Home/Service/comp3.webp';
import emergingImg from '../../assets/Home/Service/comp1.webp';
import comp5 from '../../assets/Home/Service/comp5.webp';
import comp6 from '../../assets/Home/Service/comp6.webp';
 

const RollingNumber = ({ value, duration = 2.2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20px" });
  
  // Set initial display before coming into view
  const [displayValue, setDisplayValue] = useState(() => {
    if (/B/i.test(value)) { 
      return value.replace(/(\d+)\s*B/i, "100M");
    }
    return value.replace(/\d+/, "0");
  });

  useEffect(() => {
    if (!inView) return;

    let startTime = null;
    let animationFrame;

    const isBillion = /B/i.test(value);
    const match = value.match(/^([^0-9]*)(\d+(?:\.\d+)?)(.*)$/);
    if (!match) {
      setDisplayValue(value);
      return;
    }

    const prefix = match[1] || "";
    const targetNum = parseFloat(match[2]);
    const suffix = match[3] || "";
    const totalDuration = duration * 1000;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / totalDuration, 1);

      // Smooth ease-out cubic/quart curve
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(1 - progress, 3);

      if (isBillion) {
        // Count up increasing from $100M+ up through millions and transition to $1B+
        const switchThreshold = 0.85;
        if (easeProgress < switchThreshold) {
          const mProgress = easeProgress / switchThreshold;
          // Scale from 100M to 995M
          const currentM = Math.round(100 + mProgress * (995 - 100));
          const mSuffix = suffix.replace(/B/i, "M");
          setDisplayValue(`${prefix}${currentM}${mSuffix}`);
        } else {
          // Reached $1B+
          setDisplayValue(value);
        }
      } else {
        // Standard count-up from 0 to targetNum
        const currentVal = Math.round(easeProgress * targetNum);
        setDisplayValue(`${prefix}${currentVal}${suffix}`);
      }

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [inView, value, duration]);

  return (
    <motion.span
      ref={ref}
      className="stat-counter-value"
      initial={{ opacity: 0.7, scale: 0.98 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5 }}
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {displayValue}
    </motion.span>
  );
};

const HomeSolutions = () => {
  const solutions = [
    {
      id: "service-1",
      title: "Financial Consultancy & Advisory",
      description: "Project finance, investment planning, refinancing, and financial due diligence for renewable energy projects.",
      image: financeImg,
      link: "/services/finance",
    },
    {
      id: "service-2",
      title: "Technical Consultancy, Design and Engineering",
      description: "Across Solar, Energy Storage, Green Hydrogen and Power Conversion Systems",
      image: projectImg,
      link: "/services/project-development",
    },
    {
      id: "service-3",
      title: "Turnkey EPC & Project Execution",
      description: "From design to commissioning for a cleaner tomorrow",
      image: epcImg,
      link: "/services/epc-coordination",
    },
    {
      id: "service-4",
      title: "BESS, Hybrid & Green Hydrogen",
      description: "Integrated solutions for a cleaner and sustainable tomorrow",
      image: emergingImg,
      link: "/services/emerging",
    },
    {
      id: "service-5",
      title: "Manufacturing",
      description: "From concept to commissioning, we deliver integrated solutions across Solar, BESS, Green Hydrogen, Power Conversion and Cable Manufacturing.",
      image: comp5,
      link: "/services/manufacturing",
    },
    {
      id: "service-6",
      title: "Medical Services",
      description: "Critical power reliability, renewable energy integration, and specialized infrastructure solutions for healthcare facilities and medical institutions.",
      image: comp6,
      link: "/services/medical-services",
    },
  ];

  return (
    <section className="home-solutions-section" id="services">
      {/* About Us Summary Section */}
      <div className="about-summary-wrapper">
        <Container>
          <Row className="align-items-start">
            <Col lg={7} md={12} className="about-text-col">
              <span className="section-tag" style={{color:"white"}}>About Us</span>
              <h2 className="about-main-heading">
                supporting renewable energy projects through finance, engineering, and execution expertise.
              </h2>
            </Col>
            <Col lg={5} md={12} className="stats-col">
              <div className="stats-container">
                <div className="stat-item">
                  <span className="stat-label1">Renewable transactions supported</span>
                  <div className="stat-value">
                    <RollingNumber value="$1B+" />
                  </div>
                </div>

               {/*  <div className="stat-divider"></div> */}
                <div className="stat-item">
                  <span className="stat-label1">Engineering mandates delivered</span>
                  <div className="stat-value">
                    <RollingNumber value="40+" />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        {/* Our Solutions Section */}
        <div className="solutions-header text-center">
          <div className="solutions-tag">
            <span>Our Solution</span>
          </div>
          <h2 className="solutions-main-title">
            Supporting Renewable Energy Projects <br className="d-none d-md-block" />
            from Planning to Execution
          </h2>
          <p className="solutions-subtitle">
            We help developers, investors, and businesses with technical, financial, <br className="d-none d-md-block" />
            and project support across renewable energy projects.
          </p>
         
        </div>

        {/* Solutions Grid */}
        <Row className="solutions-grid">
          {solutions.map((item, index) => (
            <Col key={index} lg={6} md={6} sm={12} className="mb-4" id={item.id}>
              <Link 
                to={item.link} 
                className="solution-card-link"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })}
              >
                <div className="solution-card">
                  <div className="card-image-wrapper">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="card-img" 
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="card-arrow-btn">
                      <BsArrowRight />
                    </div>
                  </div>
                  <div className="card-content">
                    <h4 className="card-title1">{item.title}</h4>
                    <p className="card-desc1">{item.description}</p>
                  </div>
                </div>
              </Link>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default HomeSolutions;